
require("./../../../node_modules/@module-federation/nextjs-mf/src/include-defaults.js");
/* eslint-disable no-console */
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import sanitizer from 'string-sanitizer';
import authCookie from 'utils/authCookie';
import streamApi from 'lib/api/stream';
import MainLayout from 'global/MainLayout';
import { GuestDocumentLayout } from 'features/company/styled';
import companyApi from 'lib/api/company';
import { StreamPostData } from '../../../@types/stream';

const Company = dynamic(() => import('features/company'));
const GuestFRCompany = dynamic(
  () => import('features/company/FRPage/GuestCompany'),
);
const CompanyGuestStream = dynamic(
  () => import('features/company/GuestCompany'),
);

interface SymbolPageProps {
  type: 'FR' | 'Default';
  symbol: string;
  posts?: StreamPostData[];
  company?: any;
  companyProfile?: any;
  chart?: any;
  findData?: any;
  isLoggedIn: boolean;
}

const SymbolPage = ({
  type,
  symbol,
  posts,
  company,
  companyProfile,
  chart,
  findData,
  isLoggedIn,
}: SymbolPageProps) => {
  const router = useRouter();
  const { param } = router.query;
  const activeTab = param?.[0];

  if (!isLoggedIn) {
    const isCompanyStock = company?.type_company?.toLowerCase() === 'saham';
    return (
      <GuestDocumentLayout noBox isCompanyStock={isCompanyStock}>
        {type === 'FR' ? (
          <GuestFRCompany
            symbol={symbol}
            company={company}
            chart={chart}
            finItem={findData?.fr_fitem}
          />
        ) : (
          <CompanyGuestStream
            symbol={symbol}
            posts={posts}
            company={company}
            isCompanyStock={isCompanyStock}
            companyProfile={companyProfile}
            chart={chart}
          />
        )}
      </GuestDocumentLayout>
    );
  }

  return (
    <MainLayout maxWidth={970}>
      <Company symbol={symbol} tab={activeTab} />
    </MainLayout>
  );
};

const fetchFRPageInfo = async (symbol: string) => {
  try {
    const promises = [
      companyApi.getGuestCompanyV2(symbol, 'IDX'),
      companyApi.getGuestCompanyChartV2(symbol, '1y'),
      companyApi.getGuestCompanyFinItem(symbol, 'IDX'),
    ];
    const results = await Promise.allSettled(promises);

    const props = {
      type: 'FR',
      symbol,
      company: null, // Initialize with default values in case of errors
      chart: null,
      findData: null,
    };

    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        const data = result.value.data?.data;
        switch (index) {
          case 0:
            props.company = data;
            break;
          case 1:
            props.chart = data;
            break;
          case 2:
            props.findData = data;
            break;
          default:
            break;
        }
      } else {
        throw new Error('Api Failed to fetch');
      }
    });

    return {
      props,
    };
  } catch (error) {
    return { notFound: true };
  }
};

const sanitizeSymbol = (_symbol: string): string => {
  let symbol = _symbol.replace('-', ' ');
  symbol = sanitizer.sanitize.keepSpace(symbol);
  return symbol.replace(' ', '-').toUpperCase();
};

export async function getServerSideProps(context) {
  const { params } = context;
  let { symbol } = params;
  symbol = sanitizeSymbol(symbol);
  const isLoggedIn = Boolean(authCookie.getIsLoggedIn(context));

  if (isLoggedIn) {
    return {
      props: {
        symbol,
        isLoggedIn,
        type: 'Default',
      },
    };
  }

  try {
    /// Default Props
    const props: SymbolPageProps = {
      type: 'Default',
      posts: [],
      symbol,
      company: null,
      companyProfile: { background: '' },
      chart: null,
      isLoggedIn,
    };

    /// Company Overview Data
    // eslint-disable-next-line no-useless-catch
    try {
      const symbolResponse = await streamApi.getGuestCompany(symbol);
      if (symbolResponse.data) {
        const companyResponse = symbolResponse.data.data;

        if (companyResponse.type_company === 'FR') {
          return fetchFRPageInfo(symbol);
        }

        props.company = {
          ...companyResponse,
          lastprice: companyResponse.price,
          change: companyResponse.change,
          percentage_change: companyResponse.percentage,
        };
      }
    } catch (err) {
      // Error from this API should return to 404 page
      throw err;
    }
    /// ---------------------------------------------------

    /// Company Profile Data
    try {
      const companyProfileResponse = await streamApi.getGuestCompanyProfile(
        symbol,
      );
      if (companyProfileResponse.data) {
        const { background } = companyProfileResponse.data.data;
        props.companyProfile.background = background.replace(/"/g, "'");
      }
    } catch (error) {
      console.error(error);
    }
    /// ---------------------------------------------------

    /// Comapany Chart Data
    try {
      const chartResponse = await streamApi.getGuestChart(symbol);
      if (chartResponse.data) {
        const { data } = chartResponse.data;
        // data might be null, e.g. CPO timeframe 1d
        props.chart = data ? { ...data } : null;
        // Inject Previous Price to 'today' prices
        // https://stockbit.atlassian.net/browse/IDT-501
        if (
          data &&
          data.timeframe === 'today' &&
          data.prices.length &&
          data.previous
        ) {
          props.chart.prices = [
            {
              date: '0',
              formatted_date: '',
              xlabel: '0',
              value: data.previous.toString(),
              percentage: '0.00',
              change: 0,
            },
            ...data.prices,
          ];
        }
      }
    } catch (error) {
      console.error(error);
    }
    /// ---------------------------------------------------

    /// Comapany Stream Data
    try {
      const postResponse = await streamApi.getGuestStream(symbol);
      if (postResponse.data) {
        props.posts = postResponse.data.data;
      }
    } catch (error) {
      console.error(error);
    }
    /// ---------------------------------------------------

    return { props };
  } catch (error) {
    return { notFound: true };
  }
}

export default SymbolPage;
